import {
  BuildingIcon,
  CaretLeftIcon,
  CubeIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
} from '@phosphor-icons/react';

import { CopyToClipboard } from '@/components/common/copy-to-clipboard';
import { OverflowTooltip } from '@/components/common/overflow-tooltip';
import shared from '@/components/layout/customer-detail-header/customer-detail-header.module.scss';
import { UserStatusChip } from '@/features/admin-users';
import { formatPhoneUS } from '@/lib/utils/format';

import type { DealerProfile } from '../types';

import styles from './dealer-detail-header.module.scss';

export type DealerDetailHeaderProps = {
  dealer: DealerProfile;
  /** The page owns the back target — it is the listing URL the admin came from (ticket 09). */
  onBack: () => void;
};

/**
 * Dealer detail profile header (Figma 1923:862322). The Customer header's chrome without its
 * overflow menu and address: Company Name sits in the contact row (Figma 2006:495598), and the
 * order count is plain text, not a link.
 */
export function DealerDetailHeader({ dealer, onBack }: DealerDetailHeaderProps) {
  return (
    <header className={shared.band}>
      <div className={shared.left}>
        <button type="button" className={shared.back} aria-label="Back to dealers" onClick={onBack}>
          <CaretLeftIcon size={24} />
        </button>
        <div className={shared.identity}>
          <div className={shared.nameRow}>
            <OverflowTooltip title={dealer.dealerName}>
              <h1 className={shared.name}>{dealer.dealerName}</h1>
            </OverflowTooltip>
            <span className={shared.statusGroup}>
              {/* The listing's own chip, so both surfaces label a status identically. */}
              <UserStatusChip status={dealer.status} />
              <span className={shared.id}>{dealer.uniqueId}</span>
              <CopyToClipboard value={dealer.uniqueId} confirmTooltip="Copied to clipboard" />
            </span>
          </div>
          <div className={shared.contacts}>
            <span className={shared.contact}>
              <PhoneIcon size={16} aria-hidden />
              {`+1 ${formatPhoneUS(dealer.phone)}`}
            </span>
            <span className={shared.contact}>
              <EnvelopeIcon size={16} aria-hidden />
              {dealer.email}
            </span>
            <span className={shared.contact}>
              <BuildingIcon size={16} aria-hidden />
              {dealer.companyName}
            </span>
            <span className={shared.contact}>
              <MapPinIcon size={16} aria-hidden />
              {dealer.cityLabel}
            </span>
          </div>
        </div>
      </div>
      <span className={`${shared.orderCount} ${styles.orderCount}`}>
        <CubeIcon size={16} aria-hidden />
        {dealer.ordersCount} orders
      </span>
    </header>
  );
}
